// E8HalvingSequenceClosedTask.js
// Zadanie zamkniete E8 (2025 CZE zad. 2) - ciag, w ktorym kazda liczba jest polowa poprzedniej.
// k-ty wyraz = start / 2^(k-1). Pytamy o n-ty wyraz (ulamek).

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function gcd(a, b) {
  return b ? gcd(b, a % b) : a;
}

function fracLatex(num, den) {
  const g = gcd(num, den);
  const n = num / g;
  const d = den / g;
  return d === 1 ? `${n}` : `\\frac{${n}}{${d}}`;
}

// wartosc k-tego wyrazu jako {num, den} (przed skroceniem)
function termFraction(start, k) {
  return { num: start, den: 2 ** (k - 1) };
}

function buildData({ start, n, shuffleOptions = true }) {
  // opcje: cztery kolejne wyrazy wokol n: pozycje n-2, n-1, n, n+1
  const positions = [n - 2, n - 1, n, n + 1];
  let entries = positions.map((k) => {
    const f = termFraction(start, k);
    return { k, latex: fracLatex(f.num, f.den), correct: k === n };
  });
  if (shuffleOptions) entries = shuffle(entries);

  const correctLatex = fracLatex(termFraction(start, n).num, termFraction(start, n).den);
  const options = entries.map((e, i) => ({ ...e, letter: LETTERS[i] }));
  const correctIndex = options.findIndex((o) => o.correct);

  return { start, n, correctLatex, options, correctIndex };
}

function createOriginalTask() {
  return {
    type: "E8HalvingSequenceClosedTask",
    data: buildData({ start: 6, n: 5, shuffleOptions: false }),
    source: { variant: "original", exam: "E8", session: "2025_CZE", taskNumber: 2 },
    tags: ["e8", "ulamki", "ciag", "dzialania-na-ulamkach"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return {
    type: "E8HalvingSequenceClosedTask",
    data: buildData({ start: pick([6, 10, 12, 14]), n: pick([4, 5, 6]) }),
    source: { variant: "random", basedOn: "E8_2025_CZE_2" },
    tags: ["e8", "ulamki", "ciag", "dzialania-na-ulamkach"],
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>Zapisywano kolejno liczby według następującej zasady:</p>
      <ul style={{ marginTop: 0 }}>
        <li>liczbę <InlineMath math={`${data.start}`} /> zapisano jako pierwszą</li>
        <li>każda następna zapisana liczba była połową liczby poprzedniej.</li>
      </ul>
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>{data.n === 5 ? "Piątą" : `${data.n}.`} liczbą, którą zapisano, jest</p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.k}>
            <ChoiceOptionMath letter={LETTERS[index]} math={option.latex} display />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({ label: LETTERS[index], textNode: LETTERS[index] }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={data.correctLatex} display />;
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return <ChoiceOptionMath letter={LETTERS[userAnswer]} math={option.latex} display />;
}

export function renderExplanation({ data }) {
  const { start, n } = data;
  const steps = [];
  for (let k = 1; k <= n; k += 1) {
    const f = termFraction(start, k);
    steps.push(fracLatex(f.num, f.den));
  }
  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Każdy kolejny wyraz powstaje przez podzielenie
        poprzedniego przez <InlineMath math="2" />.
      </p>
      <BlockMath math={steps.join(",\\ ")} />
      <p>
        Zatem {n === 5 ? "piątą" : `${n}.`} liczbą jest{" "}
        <InlineMath math={data.correctLatex} /> — odpowiedź{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
